/**
 * Phase 7-B — Buyer escrow tracking with Active / Completed / Disputed tabs.
 *
 * - GET /marketplace/escrows?role=buyer (`02_api_spec.md §6` listing variant)
 * - POST /marketplace/escrows/{id}/confirm — buyer signs ConfirmReceipt
 * - POST /marketplace/escrows/{id}/dispute — buyer signs DisputeReceipt
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import Modal from '../../components/Modal';
import FormField from '../../components/FormField';
import Badge from '../../components/Badge';
import {
  buildConfirmReceiptTypedData,
  buildDisputeReceiptTypedData,
} from '@infinia/ida-sdk';
import type { Escrow, MarketplaceConfig, WalletPubkey } from '@infinia/ida-sdk';
import {
  weiToAdi,
  escrowStatusBadge,
  shortAddr,
  BUYER_ACTIVE_ESCROW_STATES,
  BUYER_COMPLETED_ESCROW_STATES,
  BUYER_DISPUTED_ESCROW_STATES,
} from './utils';
import { encodePathSegment } from '../../lib/url';

type Tab = 'active' | 'completed' | 'disputed';

const tabFilter: Record<Tab, ReadonlySet<string>> = {
  active: BUYER_ACTIVE_ESCROW_STATES,
  completed: BUYER_COMPLETED_ESCROW_STATES,
  disputed: BUYER_DISPUTED_ESCROW_STATES,
};

function randomNonceHex(): string {
  // 32 random bytes as hex.
  const bytes = new Uint8Array(32);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 32; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return '0x' + Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function evidenceCidB64(blob: string): Promise<string> {
  // SHA-256 over the base64-encoded blob, hex 0x-prefixed.
  const data = new TextEncoder().encode(blob);
  const buf = await crypto.subtle.digest('SHA-256', data);
  const arr = Array.from(new Uint8Array(buf));
  return '0x' + arr.map((b) => b.toString(16).padStart(2, '0')).join('');
}

export default function MyEscrows() {
  const { t } = useTranslation(['marketplace', 'common']);
  const [tab, setTab] = useState<Tab>('active');
  const [items, setItems] = useState<Escrow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [config, setConfig] = useState<MarketplaceConfig | null>(null);
  const [pubkey, setPubkey] = useState<WalletPubkey | null>(null);

  const [modal, setModal] = useState<'confirm' | 'dispute' | null>(null);
  const [active, setActive] = useState<Escrow | null>(null);
  const [reason, setReason] = useState('');
  const [evidenceText, setEvidenceText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [esc, cfg] = await Promise.all([
        apiFetch<{ items: Escrow[] }>('/marketplace/escrows?role=buyer&limit=100'),
        apiFetch<MarketplaceConfig>('/marketplace/config').catch(() => null),
      ]);
      setItems(esc.items ?? []);
      if (cfg) setConfig(cfg);
      try {
        const pk = await apiFetch<WalletPubkey>('/wallets/me/marketplace-pubkey');
        setPubkey(pk);
      } catch { /* lazy */ }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('myEscrows.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!toast) return;
    const h = window.setTimeout(() => setToast(null), 4500);
    return () => window.clearTimeout(h);
  }, [toast]);

  const filtered = useMemo(() => items.filter((e) => tabFilter[tab].has(e.status)), [items, tab]);

  const openConfirm = (e: Escrow) => {
    setActive(e);
    setFormError(null);
    setModal('confirm');
  };
  const openDispute = (e: Escrow) => {
    setActive(e);
    setReason('');
    setEvidenceText('');
    setFormError(null);
    setModal('dispute');
  };
  const close = () => {
    setActive(null);
    setReason('');
    setEvidenceText('');
    setFormError(null);
    setModal(null);
  };

  const submitConfirm = async () => {
    if (!active || !config || !pubkey) {
      setFormError(t('myEscrows.errors.confirmFailed'));
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const receipt = {
        buyer: pubkey.address,
        escrowId: active.id,
        deliveryDigest: active.deliveryDigest ?? '0x' + '0'.repeat(64),
        deadline: Math.floor(Date.now() / 1000) + 600,
        nonce: randomNonceHex(),
      };
      const typedData = buildConfirmReceiptTypedData(receipt, config.domain);
      const sig = await apiFetch<{ signature: string }>('/wallets/me/sign-eip712', {
        method: 'POST', body: JSON.stringify({ typedData }),
      });
      await apiFetch(`/marketplace/escrows/${encodePathSegment(active.id)}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ confirmReceipt: receipt, signature: sig.signature }),
      });
      setToast({ kind: 'success', msg: t('myEscrows.toasts.confirmed') });
      close();
      await refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('myEscrows.errors.confirmFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitDispute = async () => {
    if (!active || !config || !pubkey) {
      setFormError(t('myEscrows.errors.disputeFailed'));
      return;
    }
    if (!reason.trim()) {
      setFormError(t('myEscrows.disputeModal.reasonPlaceholder'));
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const blobB64 = btoa(unescape(encodeURIComponent(evidenceText || reason)));
      const evidenceCid = await evidenceCidB64(blobB64);
      const receipt = {
        buyer: pubkey.address,
        escrowId: active.id,
        reason: reason.slice(0, 256),
        evidenceCid,
        deadline: Math.floor(Date.now() / 1000) + 600,
        nonce: randomNonceHex(),
      };
      const typedData = buildDisputeReceiptTypedData(receipt, config.domain);
      const sig = await apiFetch<{ signature: string }>('/wallets/me/sign-eip712', {
        method: 'POST', body: JSON.stringify({ typedData }),
      });
      await apiFetch(`/marketplace/escrows/${encodePathSegment(active.id)}/dispute`, {
        method: 'POST',
        body: JSON.stringify({
          disputeReceipt: receipt,
          signature: sig.signature,
          evidence: { kind: 'transcript_redacted', blob: blobB64, contentType: 'text/plain' },
        }),
      });
      setToast({ kind: 'success', msg: t('myEscrows.toasts.disputed') });
      close();
      await refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : t('myEscrows.errors.disputeFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('myEscrows.title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('myEscrows.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border-subtle">
        {(['active', 'completed', 'disputed'] as Tab[]).map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-bold ${tab === k ? 'border-b-2 border-primary text-primary' : 'text-text-muted hover:text-text-main'}`}
          >
            {t(`myEscrows.tabs.${k}`)}
          </button>
        ))}
      </div>

      {toast && (
        <div className={`rounded-lg border px-4 py-2 text-xs font-semibold ${toast.kind === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
          {toast.msg}
        </div>
      )}

      <div className="rounded-xl border border-border-subtle bg-white shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-subtle bg-slate-50/50">
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myEscrows.table.escrowId')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myEscrows.table.listing')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myEscrows.table.operator')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myEscrows.table.amount')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myEscrows.table.status')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myEscrows.table.deadline')}</th>
              <th className="px-5 py-2.5 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-text-faint">{t('common:loading')}</td></tr>
            ) : error ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-red-500">{error}</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-text-faint">{t('myEscrows.empty')}</td></tr>
            ) : filtered.map((e) => {
              const dl = e.windowDeadlines?.disputeDeadline ?? e.windowDeadlines?.timeoutDeadline ?? null;
              return (
                <tr key={e.id} className="border-b border-border-subtle hover:bg-slate-50/60">
                  <td className="px-5 py-3 font-mono text-xs">{shortAddr(e.id, 8)}</td>
                  <td className="px-5 py-3 font-mono text-xs">{shortAddr(e.listingId, 6)}</td>
                  <td className="px-5 py-3 font-mono text-xs">{shortAddr(e.operatorDid ?? e.operator, 6)}</td>
                  <td className="px-5 py-3 text-xs font-bold text-text-main">{weiToAdi(e.lockedAmountWei)} ADI</td>
                  <td className="px-5 py-3">
                    <Badge variant={escrowStatusBadge(e.status)}>
                      {t(`myEscrows.statusLabels.${e.status}`)}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-xs text-text-muted">{dl ? new Date(dl).toLocaleString() : '—'}</td>
                  <td className="px-5 py-3">
                    {e.status === 'claimed' && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openConfirm(e)}
                          className="rounded-lg bg-emerald-600 px-2 py-1 text-[11px] font-bold text-white hover:bg-emerald-700"
                        >
                          {t('myEscrows.actions.confirm')}
                        </button>
                        <button
                          onClick={() => openDispute(e)}
                          className="rounded-lg bg-red-600 px-2 py-1 text-[11px] font-bold text-white hover:bg-red-700"
                        >
                          {t('myEscrows.actions.dispute')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirm modal */}
      <Modal open={modal === 'confirm'} onClose={close} title={t('myEscrows.confirmModal.title')}>
        <div className="space-y-4">
          <p className="text-sm text-text-muted">
            {t('myEscrows.confirmModal.body', { amount: active ? weiToAdi(active.lockedAmountWei) : '0' })}
          </p>
          {formError && <p className="text-xs font-semibold text-red-500">{formError}</p>}
          <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
            <button onClick={close} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50">
              {t('common:cancel')}
            </button>
            <button onClick={submitConfirm} disabled={submitting} className="btn-primary text-xs disabled:opacity-50">
              {submitting ? t('myEscrows.confirmModal.submitting') : t('myEscrows.confirmModal.submit')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Dispute modal */}
      <Modal open={modal === 'dispute'} onClose={close} wide title={t('myEscrows.disputeModal.title')}>
        <div className="space-y-4">
          <p className="text-sm text-text-muted">{t('myEscrows.disputeModal.body')}</p>
          <FormField
            label={t('myEscrows.disputeModal.reason')}
            placeholder={t('myEscrows.disputeModal.reasonPlaceholder')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <FormField
            label={t('myEscrows.disputeModal.evidence')}
            as="textarea"
            placeholder={t('myEscrows.disputeModal.evidencePlaceholder')}
            value={evidenceText}
            onChange={(e) => setEvidenceText(e.target.value)}
          />
          {formError && <p className="text-xs font-semibold text-red-500">{formError}</p>}
          <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
            <button onClick={close} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50">
              {t('common:cancel')}
            </button>
            <button
              onClick={submitDispute}
              disabled={submitting}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? t('myEscrows.disputeModal.submitting') : t('myEscrows.disputeModal.submit')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
